import React, { memo } from 'react'
import { useGLTF } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { GLTFLoader } from 'three-stdlib'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { cloneWithIndependentMaterials } from './three/cloneObject'
import { disposeObject } from './three/disposeObject'
import { beginSceneWork, endSceneWork } from './three/sceneReadiness'

const extendLoaderWithMeshopt = (loader: GLTFLoader) => {
  loader.setMeshoptDecoder(MeshoptDecoder)
}

export interface MetallicOverride {
  color?: string
  metalness?: number
  roughness?: number
  envMapIntensity?: number
}

export const GLBModel = memo(function GLBModel({
  modelUrl,
  modelPosition = [0, 0, 0],
  modelScale = [1, 1, 1],
  modelRotation = [0, 0, 0],
  overrideColorHex,
  shouldCastShadow = true,
  shouldReceiveShadow = true,
  forceFlatColorHex,
  useLambertWhiteMaterial,
  metallicOverride,
  useDraco,
  onClick,
  userData,
}: {
  modelUrl: string
  modelPosition?: [number, number, number]
  modelScale?: [number, number, number]
  modelRotation?: [number, number, number]
  overrideColorHex?: string
  shouldCastShadow?: boolean
  shouldReceiveShadow?: boolean
  forceFlatColorHex?: string
  useLambertWhiteMaterial?: boolean
  metallicOverride?: MetallicOverride
  useDraco?: boolean
  onClick?: () => void
  userData?: Record<string, unknown>
}) {
  const { scene: gltfScene } = useGLTF(
    modelUrl,
    useDraco,
    undefined,
    extendLoaderWithMeshopt
  )
  const [clonedScene, setClonedScene] = React.useState<THREE.Object3D | null>(
    null
  )
  const invalidate = useThree((s) => s.invalidate)

  // Build clone in an effect (not useMemo) so StrictMode's discarded first
  // mount disposes its clone instead of leaking it.
  // cloneWithIndependentMaterials clones geometry + materials so subsequent
  // material swaps and disposeObject don't corrupt the drei useGLTF cache.
  React.useEffect(() => {
    const token = beginSceneWork()
    const clone = cloneWithIndependentMaterials(gltfScene, {
      castShadow: shouldCastShadow,
      receiveShadow: shouldReceiveShadow,
    })
    setClonedScene(clone)
    invalidate()
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => endSceneWork(token))
    )
    return () => {
      cancelAnimationFrame(raf)
      endSceneWork(token)
      setClonedScene(null)
      disposeObject(clone)
    }
    // shadow flags are baked into the clone here, but mesh-level
    // assignment also runs in the material effect below — including them
    // in deps would re-clone the whole subtree on every shadow toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gltfScene])

  React.useEffect(() => {
    if (!clonedScene) return

    if (userData) {
      Object.assign(clonedScene.userData, userData)
    }

    const createdMaterials: THREE.Material[] = []

    clonedScene.traverse((object3D) => {
      if (!(object3D instanceof THREE.Mesh)) return
      const mesh = object3D

      const replaceMaterial = (next: THREE.Material) => {
        const previous = mesh.material
        mesh.material = next
        createdMaterials.push(next)
        if (Array.isArray(previous)) {
          previous.forEach((m) => m instanceof THREE.Material && m.dispose())
        } else if (previous instanceof THREE.Material) {
          previous.dispose()
        }
      }

      if (forceFlatColorHex) {
        replaceMaterial(
          new THREE.MeshBasicMaterial({
            color: forceFlatColorHex,
            toneMapped: false,
          })
        )
      } else if (useLambertWhiteMaterial) {
        replaceMaterial(new THREE.MeshLambertMaterial({ color: '#ffffff' }))
      } else if (metallicOverride) {
        const previous = mesh.material
        const previousColor =
          !Array.isArray(previous) &&
          previous &&
          'color' in previous &&
          previous.color instanceof THREE.Color
            ? previous.color.getHex()
            : 0xcccccc
        replaceMaterial(
          new THREE.MeshStandardMaterial({
            color: metallicOverride.color ?? previousColor,
            metalness: metallicOverride.metalness ?? 1,
            roughness: metallicOverride.roughness ?? 0.3,
            envMapIntensity: metallicOverride.envMapIntensity ?? 1,
          })
        )
      } else if (overrideColorHex) {
        const material = mesh.material
        if (
          !Array.isArray(material) &&
          'color' in material &&
          material.color instanceof THREE.Color
        ) {
          material.color.set(overrideColorHex)
        }
      }

      mesh.castShadow = shouldCastShadow
      mesh.receiveShadow = shouldReceiveShadow
    })
    invalidate()

    return () => {
      createdMaterials.forEach((m) => m.dispose())
    }
  }, [
    clonedScene,
    overrideColorHex,
    shouldCastShadow,
    shouldReceiveShadow,
    forceFlatColorHex,
    useLambertWhiteMaterial,
    metallicOverride,
    userData,
    invalidate,
  ])

  if (!clonedScene) return null

  return (
    <primitive
      object={clonedScene}
      position={modelPosition}
      scale={modelScale}
      rotation={modelRotation}
      castShadow={shouldCastShadow}
      receiveShadow={shouldReceiveShadow}
      onClick={onClick}
    />
  )
})
